import { useState } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "react-query";
import { useHeritageConnection } from "../VendorBody/VendorItems/VendorConnectionModal/hooks/useHeritageConnection";
import VendorBranches from "../VendorBody/VendorItems/VendorConnectionModal/components/Branches";
import { useAuthContext } from "contexts/auth";
import { useSnackbarContext } from "components/snackbar";
import { heritageApiErrorMessage } from "./heritageApiErrorMessage";
import { runHeritageAutoLink, describeAutoLinkResult } from "../utils/heritageAutoLink";
import { useCompanyFeature } from "hooks/useCompanyFeature";
import HeritageCredentialsSection from "./HeritageCredentialsSection";
import HeritageCatalogSyncBanner from "./HeritageCatalogSyncBanner";
import HeritageCatalogDownloadMenu, {
  type HeritageCatalogSyncKind,
} from "./HeritageCatalogDownloadMenu";
import { HERITAGE_REFRESH_STOCK_BRANCH_VENDOR_HELP } from "./heritageCatalogBranchGuidance";
import { resolveHeritageBranchDetails } from "./heritageCatalogSyncMessage";
import {
  invalidateHeritageCatalogDownloadStatus,
  useHeritageCatalogDownloadStatus,
} from "../hooks/useHeritageCatalogDownloadStatus";

export type VendorIntegrationType = "" | "heritage" | "siteone";

type Props = {
  integrationType: VendorIntegrationType;
  vendorId?: number | null;
  vendorName?: string | null;
  pendingHeritageBranchId?: number | null;
  onPendingHeritageBranchChange?: (branchId: number) => void;
  /** False on Add Vendor — Heritage connect + catalog download happen after the vendor exists. */
  showCatalogDownloads?: boolean;
  autoOpenHeritageCredentials?: boolean;
  onHeritageCredentialsAutoOpenHandled?: () => void;
  /**
   * Fired after a purchase-history download completes (whether or not the
   * auto-link step succeeded — the link page is where linking gets fixed).
   * The host uses it to navigate straight to the link page (#31).
   */
  onPurchaseHistoryDownloaded?: () => void;
};

function isHeritageTokenActive(createdAt: string | undefined, expiresIn: number | undefined): boolean {
  if (!createdAt || expiresIn == null) return false;
  const expiresAt = new Date(createdAt).getTime() + expiresIn * 1000;
  return Date.now() < expiresAt;
}

const VendorIntegrationPanel = ({
  integrationType,
  vendorId,
  vendorName,
  pendingHeritageBranchId,
  onPendingHeritageBranchChange,
  showCatalogDownloads = true,
  autoOpenHeritageCredentials = false,
  onHeritageCredentialsAutoOpenHandled,
  onPurchaseHistoryDownloaded,
}: Props) => {
  const queryClient = useQueryClient();
  const { data: heritageConnection } = useHeritageConnection();
  const auth = useAuthContext();
  const companyId = auth?.company_id ?? null;
  const companyDisplayName = auth?.company_name?.trim() || "Company";
  const { showSnackbar } = useSnackbarContext() ?? {};
  const heritageFeatureEnabled = useCompanyFeature("heritage");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackIsError, setFeedbackIsError] = useState(false);
  const [isDownloadingBranchItems, setIsDownloadingBranchItems] = useState(false);
  const [isDownloadingPurchaseHistory, setIsDownloadingPurchaseHistory] = useState(false);
  const [localCatalogSyncKind, setLocalCatalogSyncKind] = useState<HeritageCatalogSyncKind | null>(
    null
  );

  const hasCredentials = Boolean(heritageConnection?.credentials_exist);

  const vendorHeritageSettingsQuery = useQuery(
    ["heritage-vendor-settings", vendorId],
    () =>
      axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/vendors/${vendorId}/heritage/settings`
        )
        .then((res) => res.data),
    {
      enabled: integrationType === "heritage" && Boolean(vendorId) && hasCredentials,
      staleTime: 1000 * 30,
    }
  );
  const selectedBranchId =
    vendorId != null
      ? vendorHeritageSettingsQuery.data?.branchId ?? null
      : pendingHeritageBranchId ?? null;

  const catalogDownloadStatusQuery = useHeritageCatalogDownloadStatus({
    enabled: integrationType === "heritage" && showCatalogDownloads && hasCredentials,
    refetchInterval:
      integrationType === "heritage" && showCatalogDownloads && hasCredentials ? 4000 : false,
  });

  const catalogSyncInProgress =
    isDownloadingBranchItems ||
    isDownloadingPurchaseHistory ||
    Boolean(catalogDownloadStatusQuery.data?.inProgress);

  const catalogSyncKind: HeritageCatalogSyncKind | null =
    localCatalogSyncKind ??
    catalogDownloadStatusQuery.data?.syncKind ??
    (isDownloadingBranchItems ? "branch" : isDownloadingPurchaseHistory ? "purchase_history" : null);

  const isConnected =
    hasCredentials &&
    isHeritageTokenActive(
      heritageConnection?.created_at,
      heritageConnection?.expires_in
    );

  const { branchName: selectedBranchName, branchCode: selectedBranchCode } = resolveHeritageBranchDetails(
    selectedBranchId,
    vendorHeritageSettingsQuery.data,
    heritageConnection?.branches
  );

  /**
   * Link the freshly downloaded parts to the company's items.
   *
   * Runs after a download because that is the point at which there is something
   * to link, and it is what makes a download useful on its own. A failure here
   * is reported rather than swallowed: the download itself succeeded, so the
   * message says so and says linking has to be retried.
   */
  const runAutoLinkAfterDownload = async (downloadMessage: string): Promise<string> => {
    if (!vendorId) {
      setFeedback(downloadMessage);
      return downloadMessage;
    }
    try {
      const result = await runHeritageAutoLink(vendorId);
      const message = `${downloadMessage} ${describeAutoLinkResult(result)}`;
      setFeedback(message);
      return message;
    } catch (error) {
      const message = `${downloadMessage} Items could not be linked automatically — open Link Items to link them, or download again to retry.`;
      setFeedbackIsError(true);
      setFeedback(message);
      console.error("[Heritage] auto-link after download failed:", error);
      return message;
    }
  };

  const handleDownloadBranchItems = async () => {
    setFeedbackIsError(false);
    setLocalCatalogSyncKind("branch");
    setIsDownloadingBranchItems(true);
    showSnackbar?.("Branch catalog download has begun.");
    try {
      await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/download-branch-items`, {
        vendorId: vendorId ?? undefined,
        branchCode: selectedBranchCode || undefined,
      });
      await runAutoLinkAfterDownload("Heritage branch catalog downloaded.");
    } catch (error) {
      setFeedbackIsError(true);
      const errMsg =
        heritageApiErrorMessage(error) ??
        "Failed to download Heritage catalog. Check that your Customer Account ID is allowed for product list, and that Heritage is connected.";
      setFeedback(errMsg);
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        showSnackbar?.(errMsg);
      }
      console.error(error);
    } finally {
      setIsDownloadingBranchItems(false);
      setLocalCatalogSyncKind(null);
      void invalidateHeritageCatalogDownloadStatus(queryClient, companyId);
    }
  };

  const handleDownloadPurchaseHistory = async () => {
    setFeedbackIsError(false);
    setLocalCatalogSyncKind("purchase_history");
    setIsDownloadingPurchaseHistory(true);
    showSnackbar?.("Purchase history catalog download has begun.");
    let completionMessage: string | null = null;
    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/download-purchase-history-items`
      );
      completionMessage = await runAutoLinkAfterDownload("Heritage purchase history downloaded.");
    } catch (error) {
      setFeedbackIsError(true);
      const errMsg =
        heritageApiErrorMessage(error) ??
        "Failed to download Heritage purchase history. Check Heritage connection and credentials.";
      setFeedback(errMsg);
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        showSnackbar?.(errMsg);
      }
      console.error(error);
    } finally {
      setIsDownloadingPurchaseHistory(false);
      setLocalCatalogSyncKind(null);
      void invalidateHeritageCatalogDownloadStatus(queryClient, companyId);
    }
    // After the state cleanup above: navigating away unmounts this panel, so the
    // callback must run last, and the inline feedback is re-surfaced as a
    // snackbar the user can still see on the link page (#31).
    if (completionMessage != null && onPurchaseHistoryDownloaded) {
      showSnackbar?.(completionMessage);
      onPurchaseHistoryDownloaded();
    }
  };

  const handleSyncCatalogImages = async () => {
    setFeedbackIsError(false);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/sync-catalog-images`
      );
      setFeedback(data?.message ?? "Catalog images refreshed.");
    } catch (error) {
      setFeedbackIsError(true);
      setFeedback(
        heritageApiErrorMessage(error) ??
          "Failed to fetch catalog images. Check the Heritage connection and try again."
      );
      console.error("[Heritage] catalog image sync failed:", error);
    }
  };

  // Feature off: a Heritage-tagged vendor presents as an ordinary vendor.
  // Rendering the Heritage panel would only fire calls that now 404.
  if (integrationType === "heritage" && !heritageFeatureEnabled) {
    return (
      <p className="text-sm text-gray-600 m-0 mt-2">
        No distributor integration. Vendor items are managed manually.
      </p>
    );
  }

  if (integrationType === "") {
    return (
      <p className="text-sm text-gray-600 m-0 mt-2">
        No distributor integration. Vendor items are managed manually.
      </p>
    );
  }

  if (integrationType === "siteone") {
    return (
      <div className="mt-2 space-y-2">
        <p className="text-sm text-gray-700 m-0">
          This vendor will use SiteOne for item linking and pricing. Use{" "}
          <strong>Update SiteOne Item Prices</strong> in the items section to verify the vendor and
          refresh prices.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-4">
      {feedback && (
        <p
          className={`text-sm m-0 ${feedbackIsError ? "text-red-600" : "text-blue-600"}`}
          role={feedbackIsError ? "alert" : "status"}
          aria-live="polite"
        >
          {feedback}
        </p>
      )}

      <HeritageCredentialsSection
        credentialsExist={hasCredentials}
        isValidated={isConnected}
        autoOpenCredentials={autoOpenHeritageCredentials}
        onAutoOpenCredentialsHandled={onHeritageCredentialsAutoOpenHandled}
        catalogSyncInProgress={catalogSyncInProgress}
      />

      {/*
        Branches appear once Heritage has accepted the credentials. Showing them
        as soon as something was typed implies a working connection, and every
        action here — selecting a branch, downloading a catalog — needs one.
      */}
      {isConnected ? (
        <>
          {showCatalogDownloads && catalogSyncInProgress && (
            <HeritageCatalogSyncBanner
              vendorName={vendorName}
              branchName={selectedBranchName}
              branchCode={selectedBranchCode}
              companyDisplayName={companyDisplayName}
              syncKind={catalogSyncKind}
            />
          )}

          <div
            className={
              showCatalogDownloads
                ? "flex flex-wrap items-center justify-between gap-3"
                : "mb-1"
            }
          >
            <h3 className="text-sm font-semibold text-gray-900 m-0">Branches</h3>
            {showCatalogDownloads && (
              <HeritageCatalogDownloadMenu
                disabled={!isConnected || catalogSyncInProgress}
                branchDownloadDisabled={!selectedBranchId}
                branchDownloadDisabledReason={
                  !selectedBranchId ? "Select a branch first" : undefined
                }
                catalogSyncInProgress={catalogSyncInProgress}
                activeSyncKind={catalogSyncKind}
                isDownloadingBranch={isDownloadingBranchItems}
                isDownloadingPurchaseHistory={isDownloadingPurchaseHistory}
                onDownloadBranch={handleDownloadBranchItems}
                onDownloadPurchaseHistory={handleDownloadPurchaseHistory}
                onSyncImages={handleSyncCatalogImages}
              />
            )}
          </div>

          {showCatalogDownloads && (
            <p className="text-xs text-gray-600 m-0">{HERITAGE_REFRESH_STOCK_BRANCH_VENDOR_HELP}</p>
          )}

          <VendorBranches
            vendorId={vendorId}
            pendingBranchId={pendingHeritageBranchId}
            onPendingBranchChange={onPendingHeritageBranchChange}
            branchSelectionDisabled={catalogSyncInProgress}
          />
        </>
      ) : null}
    </div>
  );
};

export default VendorIntegrationPanel;
